import React, { useCallback, useEffect, useState } from 'react'
import {
  ActivityIndicator,
  FlatList,
  Image,
  Linking,
  TouchableOpacity,
  View,
} from 'react-native'
import styled from 'styled-components'
import { colors } from 'infra/color'
import { ColumnLayout, RowLayout } from 'ui/common/layout'
import { Body1, Body3, Caption } from 'ui/common/text'
import { alertError, getSecureImageUrl } from 'infra/util'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Ionicons from 'react-native-vector-icons/Ionicons'
import { StackNavigationProp } from '@react-navigation/stack'
import { MyPageStackParamList } from 'navigation/mypage-stack'
import { useMe, useUserDetail, useUserReportedPlaces } from 'api/hooks'
import { toggleFollowUser } from 'api/writes'
import { mutate } from 'swr'
import { TopNavigationBar } from 'ui/common/top-navigation-bar'
import { PROFILE_IMG_PLACEHOLDER_IMAGE } from 'image/icon'
import { observer } from 'mobx-react'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { PlaceCard } from 'ui/common/place-popover'
import { Place } from 'infra/type'
import { isValidPlace } from 'store/places'

const LIMIT = 2

type UserDetailScreenNavigationProp = StackNavigationProp<MyPageStackParamList>
type UserDetailScreenRouteProp = RouteProp<
  MyPageStackParamList,
  'UserDetailScreen'
>

export const UserDetailScreen = observer(() => {
  const insets = useSafeAreaInsets()
  const navigation = useNavigation<UserDetailScreenNavigationProp>()
  const route = useRoute<UserDetailScreenRouteProp>()
  const isOtherUser = navigation.getState()?.index > 0

  const { data: me } = useMe()

  const userId = route.params?.userId
  const targetUserId = userId || me?.id

  // Fetch specific user or me
  const { data: userDetail, isLoading } = useUserDetail(userId || me?.id)

  const isMe = !userId || (me && me.id === userId)

  // Fetch follower / following counts
  const reportedPlaceCount = userDetail?.reportedPlaceCount || 0

  const isFollowing = userDetail?.isFollowing

  const handleToggleFollow = async () => {
    if (!targetUserId || isMe) return

    const nextIsFollowing = !isFollowing

    // Optimistic Update
    mutate(
      `/users/${targetUserId}`,
      { ...userDetail, isFollowing: nextIsFollowing },
      false,
    )

    try {
      await toggleFollowUser(targetUserId, nextIsFollowing)
      mutate(`/users/${targetUserId}`) // Refresh data
      mutate(targetUserId ? `/users/${targetUserId}/followers` : null) // Refresh followers count
    } catch (e) {
      alertError(e)
      mutate(`/users/${targetUserId}`) // Revert on error
    }
  }

  const [places, setPlaces] = useState<Place[]>([])
  const [offset, setOffset] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const { data: reportedPlacesData, isLoading: isLoadingPlaces } =
    useUserReportedPlaces(targetUserId, LIMIT, offset)

  useEffect(() => {
    setPlaces([])
    setOffset(0)
  }, [targetUserId])

  useEffect(() => {
    if (Array.isArray(reportedPlacesData?.items)) {
      const validPlaces = reportedPlacesData.items.filter(isValidPlace)
      if (offset === 0) {
        setPlaces(validPlaces)
      } else {
        setPlaces((prev) => [...prev, ...validPlaces])
      }
      setIsLoadingMore(false)
    }
  }, [reportedPlacesData, offset])

  const loadMore = useCallback(() => {
    if (isLoadingMore || isLoadingPlaces) return
    if (places.length < (reportedPlacesData?.total || 0)) {
      setIsLoadingMore(true)
      setOffset((prev) => prev + LIMIT)
    }
  }, [places.length, reportedPlacesData?.total, isLoadingMore, isLoadingPlaces])

  const renderHeader = () => (
    <View
      style={{
        backgroundColor: colors.white,
        paddingTop: !isOtherUser ? 24 : 0,
      }}
    >
      <Header>
        <Image
          source={
            isLoading
              ? undefined
              : userDetail?.thumbnailImageUrl
              ? { uri: getSecureImageUrl(userDetail.thumbnailImageUrl) }
              : PROFILE_IMG_PLACEHOLDER_IMAGE
          }
          style={{
            width: 60,
            height: 60,
            borderRadius: 30,
            backgroundColor: colors.gray.v200,
          }}
        />

        <NameRow>
          <View>
            <Body1
              style={{ fontSize: 16, fontWeight: 'bold', color: colors.black }}
            >
              {userDetail?.nickname || ''}
            </Body1>
            {isMe ? (
              <ProfileEditButton
                onPress={() => navigation.navigate('UserMeEditScreen')}
                style={{
                  position: 'absolute',
                  left: '100%',
                  marginLeft: 6,
                  top: '50%',
                  marginTop: -12,
                }}
              >
                <Caption
                  numberOfLines={1}
                  style={{
                    color: colors.black,
                    fontSize: 11,
                    fontWeight: '600',
                    lineHeight: 13,
                  }}
                >
                  편집
                </Caption>
              </ProfileEditButton>
            ) : (
              <FollowButton
                onPress={handleToggleFollow}
                active={isFollowing}
                style={{
                  position: 'absolute',
                  left: '100%',
                  marginLeft: 6,
                  top: '50%',
                  marginTop: -12,
                }}
              >
                <Caption
                  numberOfLines={1}
                  style={{
                    color: isFollowing ? colors.black : colors.white,
                    fontSize: 11,
                    fontWeight: '600',
                    lineHeight: 13,
                  }}
                >
                  {isFollowing ? '팔로잉' : '팔로우'}
                </Caption>
              </FollowButton>
            )}
          </View>
        </NameRow>

        {userDetail?.instagramId ? (
          <TouchableOpacity
            onPress={() => {
              if (userDetail.instagramId) {
                Linking.openURL(
                  `https://instagram.com/${userDetail.instagramId}`,
                )
              }
            }}
          >
            <Body3
              style={{
                color: colors.gray.v600,
                marginTop: 4,
                fontSize: 13,
                lineHeight: 13 * 1.5,
              }}
            >
              @{userDetail.instagramId}
            </Body3>
          </TouchableOpacity>
        ) : null}

        <StatsRow style={{ marginTop: 20, marginBottom: 12 }}>
          <StatItem
            label='팔로워'
            value={userDetail?.followerCount?.toString() || '0'}
            style={{ flex: 1 }}
            onPress={() =>
              navigation.navigate('FollowListScreen', {
                initialTab: 'follower',
                userId: targetUserId,
              })
            }
          />
          <VerticalDivider />
          <StatItem
            label='팔로잉'
            value={userDetail?.followingCount?.toString() || '0'}
            style={{ flex: 1 }}
            onPress={() =>
              navigation.navigate('FollowListScreen', {
                initialTab: 'following',
                userId: targetUserId,
              })
            }
          />
          <VerticalDivider />
          <StatItem
            label='제보한 장소'
            value={reportedPlaceCount.toString()}
            style={{ flex: 1 }}
          />
        </StatsRow>

        {!!userDetail?.bio && (
          <Body3
            style={{
              marginTop: 12,
              lineHeight: 18,
              color: colors.black,
              fontSize: 12,
              textAlign: 'center',
            }}
          >
            {userDetail.bio}
          </Body3>
        )}
      </Header>

      <TabContainer>
        <TabButton active={true} iconName='grid' onPress={() => {}} />
      </TabContainer>

      <View style={{ height: 20, backgroundColor: colors.gray.v100 }} />
    </View>
  )

  const renderEmpty = () => {
    if (isLoadingPlaces && places.length === 0) {
      return (
        <EmptyContainer>
          <ActivityIndicator size='large' color={colors.primary} />
        </EmptyContainer>
      )
    }
    return (
      <EmptyContainer>
        <Ionicons name='location-outline' size={48} color={colors.gray.v300} />
        <Body1 style={{ color: colors.gray.v500, marginTop: 16 }}>
          아직 제보한 장소가 없어요
        </Body1>
      </EmptyContainer>
    )
  }

  return (
    <Container style={{ paddingTop: insets.top + 6 }}>
      {isOtherUser && <TopNavigationBar title='' />}
      <FlatList
        data={places}
        renderItem={({ item }) => (
          <View style={{ paddingHorizontal: 20, marginBottom: 16 }}>
            <PlaceCard data={item} />
          </View>
        )}
        keyExtractor={(item, index) => `${item.id}-${index}`}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={renderEmpty}
        ListFooterComponent={
          isLoadingMore ? (
            <ActivityIndicator
              size='small'
              color={colors.gray.v500}
              style={{ paddingVertical: 16 }}
            />
          ) : null
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.5}
        style={{ flex: 1, backgroundColor: colors.gray.v100 }}
        contentContainerStyle={{ paddingBottom: 100, flexGrow: 1 }}
      />
    </Container>
  )
})

// Sub-components
const StatItem = ({
  label,
  value,
  style,
  onPress,
}: {
  label: string
  value: string
  style?: any
  onPress?: () => void
}) => (
  <TouchableOpacity
    onPress={onPress}
    activeOpacity={0.7}
    style={[{ alignItems: 'center' }, style]}
  >
    <Body1 style={{ fontWeight: 'bold', color: colors.black, fontSize: 14 }}>
      {value}
    </Body1>
    <Caption style={{ color: colors.gray.v600, marginTop: 2, fontSize: 11 }}>
      {label}
    </Caption>
  </TouchableOpacity>
)

const TabButton = ({
  active,
  iconName,
  onPress,
}: {
  active: boolean
  iconName: string
  onPress: () => void
}) => (
  <TabButtonContainer active={active} onPress={onPress} activeOpacity={0.7}>
    <Ionicons
      name={iconName}
      size={20}
      color={active ? colors.black : colors.gray.v400}
    />
  </TabButtonContainer>
)

// Styled Components
const Container = styled(ColumnLayout)`
  flex: 1;
  background-color: ${colors.white};
`

const Header = styled(ColumnLayout)`
  padding: 20px 20px 0 20px;
  align-items: center;
`

const StatsRow = styled(RowLayout)`
  align-items: center;
  width: 100%;
  padding-horizontal: 50px;
`

const VerticalDivider = styled(View)`
  width: 1px;
  height: 24px;
  background-color: ${colors.gray.v200};
`

const NameRow = styled(RowLayout)`
  align-items: center;
  margin-top: 8px;
`

const ProfileEditButton = styled(TouchableOpacity)`
  background-color: ${colors.gray.v200};
  padding: 4px 8px;
  min-width: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`

const FollowButton = styled(TouchableOpacity)<{ active?: boolean }>`
  background-color: ${(p) => (p.active ? colors.gray.v200 : colors.primary)};
  padding: 4px 8px;
  min-width: 54px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`

const TabContainer = styled(RowLayout)`
  border-bottom-width: 1px;
  border-bottom-color: ${colors.gray.v100};
  margin-top: 24px;
  justify-content: center;
`

const TabButtonContainer = styled(TouchableOpacity)<{ active: boolean }>`
  align-items: center;
  justify-content: center;
  padding-vertical: 10px;
  width: 60px;
  border-bottom-width: 2px;
  border-bottom-color: ${(p) => (p.active ? colors.black : 'transparent')};
`

const EmptyContainer = styled(ColumnLayout)`
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: ${colors.gray.v100};
`
